import React from "react";
import FileUploadSection from "./FileUploadSection";
import SelectionDropdown from "./SelectionDropdown";
import RepresentativePeopleSection from "./RepresentativePeopleSection";

const CustomerFormFields = ({
  register,
  errors,
  formValues,
  setValue,
  watch,
  isEditMode,
  // File handlers
  handleCustomerFilesChange,
  handleRemoveCustomerFile,
  getFileName,
  customerFileError,
  // Selection dropdown props
  selectedUsersData,
  availableUsers,
  loadingUsers,
  userSearchTerm,
  setUserSearchTerm,
  showUsersDropdown,
  setShowUsersDropdown,
  // Representative people handlers
  handleAddRepresentativePerson,
  handleRemoveRepresentativePerson
}) => {
  const customerType = watch("type") || "customer";

  return (
    <>
      <div className="form-grid form-grid--single">
        <div className="form-field">
          <label htmlFor="type" className="form-label">Type *</label>
          <select
            id="type"
            className={`form-select ${errors.type ? "input-error" : ""}`}
            {...register("type", { required: "Type is required" })}
            defaultValue="customer"
            aria-invalid={!!errors.type}
          >
            <option value="customer">Customer</option>
            <option value="platform">Platform</option>
          </select>
          {errors.type && <p className="error-text">{errors.type.message}</p>}
        </div>
      </div>

      <div className="form-grid form-grid--single">
        <div className="form-field">
          <label htmlFor="companyName" className="form-label">Company Name *</label>
          {customerType === "platform" ? (
            <select
              id="companyName"
              className={`form-select ${errors.companyName ? "input-error" : ""}`}
              {...register("companyName", { required: "Company Name is required" })}
              aria-invalid={!!errors.companyName}
            >
              <option value="">Select Platform</option>
              <option value="Shiply">Shiply</option>
              <option value="CitizenShipper">CitizenShipper</option>
              <option value="Out">Out</option>
            </select>
          ) : (
            <input
              id="companyName"
              className={`form-input ${errors.companyName ? "input-error" : ""}`}
              placeholder="Company Name"
              {...register("companyName", { required: "Company Name is required" })}
              aria-invalid={!!errors.companyName}
            />
          )}
          {errors.companyName && <p className="error-text">{errors.companyName.message}</p>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? "input-error" : ""}`}
            placeholder="email@example.com"
            {...register("email", {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email format"
              }
            })}
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="error-text">{errors.email.message}</p>}
        </div>

        <div className="form-field">
          <label htmlFor="message" className="form-label">Phone Number</label>
          <input
            id="message"
            type="tel"
            className={`form-input ${errors.message ? "input-error" : ""}`}
            placeholder="Phone Number"
            {...register("message")}
            aria-invalid={!!errors.message}
          />
          {errors.message && <p className="error-text">{errors.message.message}</p>}
        </div>
      </div>

      {customerType !== "platform" && (
        <div className="form-section address-section">
          <span className="section-title">Address Information</span>
          <div className="form-grid form-grid--single">
            <div className="form-field">
              <label htmlFor="customerAddress.address" className="form-label">Address</label>
              <input
                id="customerAddress.address"
                className="form-input"
                placeholder="Street Address"
                {...register("customerAddress.address")}
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="customerAddress.city" className="form-label">City</label>
              <input
                id="customerAddress.city"
                className="form-input"
                placeholder="City"
                {...register("customerAddress.city")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="customerAddress.state" className="form-label">State</label>
              <input
                id="customerAddress.state"
                className="form-input"
                placeholder="State"
                {...register("customerAddress.state")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="customerAddress.zipCode" className="form-label">Zip Code</label>
              <input
                id="customerAddress.zipCode"
                className="form-input"
                placeholder="Zip Code"
                {...register("customerAddress.zipCode")}
              />
            </div>
          </div>
        </div>
      )}

      <div className="form-section">
        <span className="section-title">Payment Information</span>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="customerPaymentTerms" className="form-label">DTP</label>
            <select
              id="customerPaymentTerms"
              className="form-select"
              {...register("customerPaymentTerms")}
            >
              <option value="">Select DTP</option>
              {Array.from({ length: 90 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day} {day === 1 ? 'day' : 'days'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="customerCreditLimit" className="form-label">Credit Limit ($)</label>
            <input
              id="customerCreditLimit"
              type="number"
              className="form-input"
              placeholder="0.00"
              {...register("customerCreditLimit", {
                valueAsNumber: true,
                min: { value: 0, message: "Credit limit must be positive" }
              })}
              min="0"
              step="0.01"
            />
            {errors.customerCreditLimit && <p className="error-text">{errors.customerCreditLimit.message}</p>}
          </div>
        </div>
      </div>

      <FileUploadSection
        title="Customer Documents"
        formValues={formValues}
        fileFieldName="customerFiles"
        existingFilesFieldName="existingCustomerFiles"
        fileError={customerFileError}
        onFileChange={handleCustomerFilesChange}
        onRemoveFile={handleRemoveCustomerFile}
        getFileName={getFileName}
      />

      <RepresentativePeopleSection
        formValues={formValues}
        setValue={setValue}
      />

      {isEditMode && (
        <SelectionDropdown
          title="Allowed Users"
          label="Select users who can access this customer"
          selectedItems={selectedUsersData}
          availableItems={availableUsers}
          loading={loadingUsers}
          searchTerm={userSearchTerm}
          onSearchChange={setUserSearchTerm}
          formFieldName="allowedUsers"
          formValues={formValues}
          setValue={setValue}
          getItemDisplayName={(user) => 
            user.firstName && user.lastName 
              ? `${user.firstName} ${user.lastName}`
              : user.email || 'Unnamed User'
          }
          getItemId={(user) => (user.id || user._id).toString()}
          itemType="users"
          showDropdown={showUsersDropdown}
          setShowDropdown={setShowUsersDropdown}
        />
      )}
    </>
  );
};

export default CustomerFormFields;

